import {TooltipProvider} from "./ui/tooltip";
import {Search, User, Menu, Bell, LogOut, BookOpen, MessageSquare, Shield} from "lucide-react";
import {useSidebar} from "./ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./ui/dropdown-menu";
import {Avatar, AvatarFallback, AvatarImage} from "./ui/avatar";
import {Link} from "react-router-dom";
import {useResourcePersonAuth} from "../store/resourcePerson/resourcePersonStore";
import useResourcePersonNotifications from "../hooks/useResourcePersonNotifications";
import {imageUrl} from "../lib/format";

const NavBar = () => {
  const {toggleSidebar} = useSidebar();
  const {logout, user} = useResourcePersonAuth();
  const {notifications} = useResourcePersonNotifications();

  const avatarSrc =
    imageUrl(user?.avatar || user?.image) || "/images/default_profile.png";

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem("isAdmin");
    window.location.href = "/login";
  };

  return (
    <TooltipProvider>
      <header
        className="
          fixed top-0 left-0 md:left-64 
          w-full md:w-[calc(100%-16rem)] 
          h-16 border-b bg-background/70 backdrop-blur-md 
          shadow-sm flex items-center justify-between px-6 z-50
        "
      >
        {/* ===== Left Section (Hamburger + Search) ===== */}
        <div className="flex items-center gap-4  md:w-auto">
          {/* Hamburger toggle button — only on mobile */}
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-muted/30 transition-all flex items-center justify-center md:hidden"
          >
            <Menu className="h-6 w-6 text-muted-foreground" />
          </button>

          {/* Search Bar — hidden on small screens */}
          <div className="relative w-full max-w-md hidden sm:block">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search courses, students, or content..."
              className="w-full h-10 pl-11 pr-4 text-sm bg-muted/40 border border-border rounded-xl 
              focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all 
              placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {/* ===== Right Section (Notifications + Profile) ===== */}
        <div className="flex items-center gap-4">
          {/* === Notifications Dropdown === */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="relative cursor-pointer">
                <Bell className="h-6 w-6 text-muted-foreground hover:text-foreground transition-all" />
                {/* Red badge if notifications exist */}
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-[1px] rounded-full">
                    {notifications.length}
                  </span>
                )}
              </div>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="w-62 mt-2" align="end">
              <DropdownMenuLabel className="font-medium text-sm">Notifications</DropdownMenuLabel>
              <DropdownMenuSeparator />

              {notifications.length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-3">
                  No new notifications
                </p>
              ) : (
                notifications.map((n) => (
                  <DropdownMenuItem
                    key={n.id}
                    className="flex flex-col items-start py-2 hover:bg-muted/40 cursor-pointer"
                  >
                    <p className="text-sm font-medium">{n.title}</p>
                    <span className="text-xs text-muted-foreground">
                      {n.description}
                      {n.time ? ` · ${n.time}` : ""}
                    </span>
                  </DropdownMenuItem>
                ))
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link
                  to="/resource-person/notifications"
                  className="w-full text-center text-primary text-sm font-medium hover:underline"
                >
                  View All Notifications
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* === Profile Dropdown === */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="cursor-pointer">
                <Avatar className="w-12 h-12 border border-border shadow-sm hover:scale-105 transition-all">
                  <AvatarImage
                    src={avatarSrc}
                    alt="User"
                  />
                  <AvatarFallback>
                    <User className="h-5 w-5 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
              </div>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="w-56 mt-2" align="end">
              <DropdownMenuLabel>
                <div className="flex items-center gap-3">
                  <Avatar className="w-8 h-8">
                    <AvatarImage
                      src={avatarSrc}
                      alt="User"
                    />
                    <AvatarFallback>U</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium">{user?.name || "Resource Person"}</p>
                    <p className="text-xs text-muted-foreground">{user?.email || ""}</p>
                  </div>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              <DropdownMenuItem asChild>
                <Link to="/resource-person/account/settings" className="flex items-center">
                  <User className="mr-2 h-4 w-4" /> My Profile
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <Link to="/resource-person/content-library" className="flex items-center">
                  <BookOpen className="mr-2 h-4 w-4" /> My Courses
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <Link to="/resource-person/notifications" className="flex items-center">
                  <Bell className="mr-2 h-4 w-4" /> Notifications
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <Link to="/resource-person/support" className="flex items-center">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  All Messages
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <Link to="/resource-person/support" className="flex items-center">
                  <Shield className="mr-2 h-4 w-4" />
                  Message Admin
                </Link>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem asChild>
                <button
                  onClick={handleLogout}
                  className="flex items-center text-red-600 hover:text-red-700 w-full"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    </TooltipProvider>
  );
};

export default NavBar;
